/**
 * =================================================================================
 * Sevad Furniture Fittings - Express Web CMS Integration
 * =================================================================================
 */
class ProductCatalog {
    constructor() {
        this.BUSINESS_ID = window.__EXPRESS_WEB_BUSINESS_ID__ || '68f753c900197ce204ad';
        this.API_BASE_URL = window.__EXPRESS_WEB_API_BASE__ || 'https://alpha.expws.sandpark.co';

        this.products = [];
        this.categories = [];
        this.categoriesById = new Map();
        this.cmsCategories = [];
        this.cmsCategoriesById = new Map();
        this.cmsCategoriesBySlug = new Map();

        console.log('🚀 Product Catalog initialized for business:', this.BUSINESS_ID);
        this.init();
    }

    async init() {
        try {
            console.log('🔄 Loading catalog from Express Web CMS...');
            await this.loadProducts();
            await this.loadCategories();
            this.buildCategoryIndex();

            console.log('🎨 Rendering storefront content...');
            this.renderCategories();
            this.renderTrendingProducts();

            const catalogue = document.querySelector('.products-grid[data-catalogue]');
            if (catalogue) {
                this.lockedCategory = catalogue.dataset.category || '';
                this.setupProductPageListeners();
                this.renderFilters();
                this.updateProductView();
                this.injectProductStructuredData();
            }

            console.log('✅ Catalog ready!');
        } catch (error) {
            console.error('❌ Failed to initialize catalog:', error);
            this.showError(error);
        }
    }

    async loadProducts() {
        const url = this.buildProductsUrl();
        console.log('🌐 Fetching products from:', url);

        const response = await fetch(url, {
            headers: { 'Accept': 'application/json' },
            cache: 'no-store'
        });

        if (!response.ok) {
            throw new Error(`API request failed with status ${response.status}`);
        }

        let data;
        try {
            data = await response.json();
        } catch (error) {
            throw new Error('Received non-JSON response from CMS');
        }

        if (!data?.success) {
            throw new Error(data?.error || 'CMS returned an error response');
        }

        this.products = (data.products || []).map(product => this.transformProduct(product));
        console.log(`📦 Loaded ${this.products.length} product(s)`);
    }

    buildProductsUrl() {
        const base = this.API_BASE_URL.replace(/\/+$/, '');
        const businessId = encodeURIComponent(this.BUSINESS_ID);
        return `${base}/api/products?businessId=${businessId}`;
    }

    buildCategoriesUrl() {
        const base = this.API_BASE_URL.replace(/\/+$/, '');
        const businessId = encodeURIComponent(this.BUSINESS_ID);
        return `${base}/api/categories?businessId=${businessId}`;
    }

    async loadCategories() {
        const url = this.buildCategoriesUrl();
        console.log('🌐 Fetching categories from:', url);

        try {
            const response = await fetch(url, {
                headers: { 'Accept': 'application/json' },
                cache: 'no-store'
            });

            if (!response.ok) {
                throw new Error(`Category API request failed with status ${response.status}`);
            }

            let data;
            try {
                data = await response.json();
            } catch (error) {
                throw new Error('Received non-JSON response when loading categories');
            }

            if (data?.success === false) {
                throw new Error(data?.error || 'CMS returned an error response for categories');
            }

            const categoryPayload = this.extractCategoriesFromResponse(data);
            if (!Array.isArray(categoryPayload)) {
                console.warn('⚠️ Category response did not include an array payload. Falling back to product-derived categories.');
                this.cmsCategories = [];
            } else {
                this.cmsCategories = categoryPayload
                    .map(category => this.transformCategoryRecord(category))
                    .filter(Boolean);
            }

            this.cmsCategoriesById = new Map(this.cmsCategories.map(category => [category.id, category]));
            this.cmsCategoriesBySlug = new Map(this.cmsCategories.map(category => [category.slug, category]));

            console.log(`🖼️ Loaded ${this.cmsCategories.length} categor${this.cmsCategories.length === 1 ? 'y' : 'ies'} from CMS`);
        } catch (error) {
            console.error('⚠️ Failed to load categories from CMS:', error);
            this.cmsCategories = [];
            this.cmsCategoriesById = new Map();
            this.cmsCategoriesBySlug = new Map();
        }
    }

    transformProduct(product) {
        const images = Array.isArray(product.images) ? product.images : [];
        const variants = Array.isArray(product.variants) ? product.variants.filter(v => v.enabled !== false) : [];

        const transformedVariants = variants.map(variant => this.transformVariant(variant));
        const selectedAttributeIds = this.collectSelectedAttributeIds(transformedVariants);

        const categoryInfo = this.normalizeCategory(product.category);
        const categoryName = categoryInfo.name;
        const categorySlug = categoryInfo.slug;
        const description = product.description || '';

        return {
            id: product.id,
            $id: product.id,
            name: product.name || 'Product',
            description,
            short_description: this.generateShortDescription(description),
            category: categoryName,
            category_id: categorySlug,
            category_slug: categorySlug,
            category_image_url: categoryInfo.imageUrl || null,
            base_price: this.parsePrice(product.basePrice),
            hasVariants: transformedVariants.length > 0,
            images: JSON.stringify(images),
            parsedImages: images,
            variants_data: JSON.stringify(transformedVariants),
            parsedVariants: transformedVariants,
            selected_attributes: JSON.stringify(selectedAttributeIds),
            createdAt: product.createdAt,
            $createdAt: product.createdAt,
            updatedAt: product.updatedAt,
            $updatedAt: product.updatedAt
        };
    }

    extractCategoriesFromResponse(payload) {
        if (!payload) {
            return [];
        }

        if (Array.isArray(payload)) {
            return payload;
        }

        const possibleKeys = [
            'categories',
            'data',
            'items',
            'records',
            'documents',
            'results',
            'result',
            'collection',
            'collections'
        ];

        for (const key of possibleKeys) {
            const value = payload[key];
            if (Array.isArray(value)) {
                return value;
            }
        }

        for (const key of possibleKeys) {
            const value = payload[key];
            if (value && typeof value === 'object' && value !== payload) {
                const nested = this.extractCategoriesFromResponse(value);
                if (Array.isArray(nested) && nested.length > 0) {
                    return nested;
                }
            }
        }

        return [];
    }

    transformCategoryRecord(category) {
        if (!category) {
            return null;
        }

        const normalized = this.normalizeCategory(category);
        const slug = normalized.slug || this.slugify(normalized.name);
        if (!slug) {
            return null;
        }

        const id = this.extractCategoryId(category, slug);
        const description = this.extractCategoryDescription(category);
        const parentId = this.extractCategoryParentId(category);
        const rawSortOrder = category?.sortOrder ?? category?.sort_order ?? category?.order ?? category?.position ?? category?.index ?? category?.priority;
        const sortOrder = this.normaliseSortOrder(rawSortOrder);

        return {
            id,
            $id: id,
            name: normalized.name,
            slug,
            description,
            image_url: normalized.imageUrl || null,
            parentId: parentId || null,
            sortOrder,
            raw: category
        };
    }

    extractCategoryId(category, fallback) {
        const possibleKeys = ['id', '$id', '_id', 'categoryId', 'category_id', 'slug', 'uuid', 'key'];
        for (const key of possibleKeys) {
            if (key in (category || {})) {
                const value = category[key];
                if (value === null || value === undefined) continue;
                const stringValue = typeof value === 'string' ? value : value.toString?.();
                if (stringValue && stringValue.trim()) {
                    return stringValue.trim();
                }
            }
        }
        return fallback;
    }

    extractCategoryDescription(category) {
        if (!category || typeof category !== 'object') {
            return '';
        }

        const possibleKeys = ['description', 'summary', 'subtitle', 'details', 'body', 'content', 'copy'];
        for (const key of possibleKeys) {
            if (key in category) {
                const value = category[key];
                if (typeof value === 'string' && value.trim()) {
                    return value.trim();
                }
            }
        }

        if (category.metadata && typeof category.metadata === 'object') {
            const metaDescription = this.extractCategoryDescription(category.metadata);
            if (metaDescription) {
                return metaDescription;
            }
        }

        return '';
    }

    extractCategoryParentId(category) {
        if (!category || typeof category !== 'object') {
            return null;
        }

        const possibleKeys = ['parentId', 'parent_id', 'parent', 'parentCategoryId', 'parent_category_id'];
        for (const key of possibleKeys) {
            if (!(key in category)) continue;
            const value = category[key];
            if (value === null || value === undefined) continue;

            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed) {
                    return trimmed;
                }
                continue;
            }

            if (typeof value === 'number' && Number.isFinite(value)) {
                return value.toString();
            }

            if (typeof value === 'object') {
                const nestedId = this.extractCategoryId(value, null);
                if (nestedId) {
                    return nestedId;
                }
            }
        }

        return null;
    }

    normaliseSortOrder(value) {
        if (value === null || value === undefined || value === '') {
            return 0;
        }

        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }

        if (typeof value === 'string') {
            const parsed = Number(value.trim());
            if (Number.isFinite(parsed)) {
                return parsed;
            }
        }

        return 0;
    }

    resolveCMSCategory(product, slug) {
        if (slug && this.cmsCategoriesBySlug.has(slug)) {
            return this.cmsCategoriesBySlug.get(slug);
        }

        const idsToCheck = [
            product.category_id,
            product.category_slug
        ];
        for (const id of idsToCheck) {
            if (!id) continue;
            if (this.cmsCategoriesById.has(id)) {
                return this.cmsCategoriesById.get(id);
            }
        }

        if (product.category) {
            const derivedSlug = this.slugify(product.category);
            if (derivedSlug && this.cmsCategoriesBySlug.has(derivedSlug)) {
                return this.cmsCategoriesBySlug.get(derivedSlug);
            }
        }

        return null;
    }

    normalizeCategory(category) {
        const fallback = { name: 'Uncategorized', slug: 'uncategorized', imageUrl: null };

        if (!category) {
            return fallback;
        }

        if (typeof category === 'string') {
            const trimmed = category.trim();
            if (!trimmed) {
                return fallback;
            }

            const parsedCategory = (trimmed.startsWith('{') || trimmed.startsWith('['))
                ? this.safeParseJSON(trimmed, null)
                : null;
            if (parsedCategory && typeof parsedCategory === 'object') {
                return this.normalizeCategory(parsedCategory);
            }

            return {
                name: trimmed,
                slug: this.slugify(trimmed) || fallback.slug,
                imageUrl: null
            };
        }

        if (typeof category === 'object') {
            const label = (category.label || category.name || category.title || category.path || category.slug || category.id || '').toString().trim();
            const slugSource = (category.slug || label || category.id || '').toString().trim();
            const imageUrl = this.extractCategoryImage(category);

            const name = label || fallback.name;
            const slug = this.slugify(slugSource) || (category.id ? this.slugify(category.id.toString()) : fallback.slug);

            return {
                name,
                slug: slug || fallback.slug,
                imageUrl: imageUrl || null
            };
        }

        const stringValue = category.toString().trim();
        if (!stringValue) {
            return fallback;
        }

        return {
            name: stringValue,
            slug: this.slugify(stringValue) || fallback.slug,
            imageUrl: null
        };
    }

    extractCategoryImage(category) {
        if (!category || typeof category !== 'object') {
            return null;
        }

        const possibleKeys = [
            'image_url',
            'imageUrl',
            'image',
            'coverImage',
            'cover_image',
            'thumbnail',
            'thumbnailUrl',
            'thumbnail_url',
            'media',
            'mediaUrl',
            'media_url',
            'icon',
            'iconUrl',
            'icon_url'
        ];

        for (const key of possibleKeys) {
            if (key in category) {
                const resolved = this.resolveImageValue(category[key]);
                if (resolved) {
                    return resolved;
                }
            }
        }

        if (category.metadata && typeof category.metadata === 'object') {
            const metadataImage = this.resolveImageValue(
                category.metadata.image || category.metadata.imageUrl || category.metadata.image_url
            );
            if (metadataImage) {
                return metadataImage;
            }
        }

        return null;
    }

    resolveImageValue(value) {
        if (!value) {
            return null;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) {
                return null;
            }

            if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                const parsed = this.safeParseJSON(trimmed, null);
                if (parsed) {
                    const resolved = this.resolveImageValue(parsed);
                    if (resolved) {
                        return resolved;
                    }
                }
            }

            if (this.looksLikeUrl(trimmed)) {
                return trimmed;
            }

            return null;
        }

        if (Array.isArray(value)) {
            for (const item of value) {
                const resolved = this.resolveImageValue(item);
                if (resolved) {
                    return resolved;
                }
            }
            return null;
        }

        if (typeof value === 'object') {
            const prioritizedKeys = [
                'url',
                'href',
                'src',
                'path',
                'downloadUrl',
                'cdnUrl',
                'fileUrl',
                'publicUrl',
                'original',
                'default'
            ];

            for (const key of prioritizedKeys) {
                if (key in value) {
                    const resolved = this.resolveImageValue(value[key]);
                    if (resolved) {
                        return resolved;
                    }
                }
            }

            for (const key of Object.keys(value)) {
                if (prioritizedKeys.includes(key)) {
                    continue;
                }
                const resolved = this.resolveImageValue(value[key]);
                if (resolved) {
                    return resolved;
                }
            }
        }

        return null;
    }

    looksLikeUrl(value) {
        if (typeof value !== 'string') {
            return false;
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return false;
        }

        if (/^(https?:)?\/\//i.test(trimmed)) return true;
        if (/^data:image\//i.test(trimmed)) return true;
        if (/^blob:/i.test(trimmed)) return true;
        if (trimmed.startsWith('/')) return true;
        if (trimmed.startsWith('./') || trimmed.startsWith('../')) return true;

        return /\.(png|jpe?g|gif|webp|svg|avif|heic|heif)$/i.test(trimmed);
    }

    transformVariant(variant) {
        const rawAttributes = this.normaliseVariantAttributes(variant.attributes);
        const attributeEntries = Object.entries(rawAttributes);

        const attributeMap = {};
        attributeEntries.forEach(([attributeName, value]) => {
            const safeName = attributeName || 'Option';
            const attributeId = this.slugify(safeName) || safeName;

            attributeMap[attributeId] = {
                attributeId,
                attributeName: safeName,
                value: value ?? ''
            };
        });

        return {
            id: variant.id,
            sku: variant.sku ?? null,
            name: variant.variantName || attributeEntries.map(([, val]) => val).join(' - '),
            price: this.parsePrice(variant.price),
            enabled: variant.enabled !== false,
            attributes: attributeMap
        };
    }

    normaliseVariantAttributes(attributes) {
        if (!attributes) return {};
        if (typeof attributes === 'string') {
            return this.safeParseJSON(attributes, {});
        }
        if (typeof attributes === 'object') {
            return attributes;
        }
        return {};
    }

    collectSelectedAttributeIds(variants) {
        const ids = new Set();
        variants.forEach(variant => {
            Object.keys(variant.attributes || {}).forEach(id => ids.add(id));
        });
        return Array.from(ids);
    }

    buildCategoryIndex() {
        const categoryMap = new Map();

        this.products.forEach(product => {
            const slug = product.category_id || 'uncategorized';
            const cmsCategory = this.resolveCMSCategory(product, slug);
            const name = cmsCategory?.name || product.category || 'Uncategorized';
            const imageUrl = cmsCategory?.image_url || product.category_image_url || null;
            const description = cmsCategory?.description || '';
            const sortOrder = cmsCategory ? this.normaliseSortOrder(cmsCategory.sortOrder) : 0;
            const cmsId = cmsCategory?.id || null;
            if (!categoryMap.has(slug)) {
                categoryMap.set(slug, {
                    $id: slug,
                    name,
                    slug,
                    description,
                    image_url: imageUrl,
                    sortOrder,
                    cms_id: cmsId,
                    count: 0
                });
            }
            const entry = categoryMap.get(slug);
            entry.count += 1;
            if (cmsCategory) {
                if (!entry.description && description) {
                    entry.description = description;
                }
                if (!entry.image_url && imageUrl) {
                    entry.image_url = imageUrl;
                }
                if (cmsCategory.name && entry.name !== cmsCategory.name) {
                    entry.name = cmsCategory.name;
                }
                entry.cms_id = cmsCategory.id || entry.cms_id;
                entry.sortOrder = this.normaliseSortOrder(cmsCategory.sortOrder);
            } else if (!entry.image_url && imageUrl) {
                entry.image_url = imageUrl;
            }
        });

        this.cmsCategories.forEach(cmsCategory => {
            if (!cmsCategory.slug) {
                return;
            }
            if (!categoryMap.has(cmsCategory.slug)) {
                categoryMap.set(cmsCategory.slug, {
                    $id: cmsCategory.slug,
                    name: cmsCategory.name || 'Uncategorized',
                    slug: cmsCategory.slug,
                    description: cmsCategory.description || '',
                    image_url: cmsCategory.image_url || null,
                    sortOrder: this.normaliseSortOrder(cmsCategory.sortOrder),
                    cms_id: cmsCategory.id || null,
                    count: 0
                });
            } else {
                const entry = categoryMap.get(cmsCategory.slug);
                if (cmsCategory.name && entry.name !== cmsCategory.name) {
                    entry.name = cmsCategory.name;
                }
                if (cmsCategory.description && !entry.description) {
                    entry.description = cmsCategory.description;
                }
                if (cmsCategory.image_url) {
                    entry.image_url = entry.image_url || cmsCategory.image_url;
                }
                entry.sortOrder = this.normaliseSortOrder(cmsCategory.sortOrder);
                entry.cms_id = cmsCategory.id || entry.cms_id;
            }
        });

        this.categories = Array.from(categoryMap.values()).sort((a, b) => {
            const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
            if (orderDiff !== 0) {
                return orderDiff;
            }
            return a.name.localeCompare(b.name);
        });
        this.categoriesById = new Map(this.categories.map(category => [category.$id, category]));

        console.log(`📚 Identified ${this.categories.length} categor${this.categories.length === 1 ? 'y' : 'ies'}`);
    }

    setupProductPageListeners() {
        document.getElementById('search-input')?.addEventListener('input', () => this.updateProductView());
        document.getElementById('category-select')?.addEventListener('change', () => this.updateProductView());
        document.getElementById('sort-select')?.addEventListener('change', () => this.updateProductView());
        document.getElementById('clear-filters-btn')?.addEventListener('click', () => this.clearFilters());
    }

    clearFilters() {
        console.log('🧹 Clearing all filters...');
        const searchInput = document.getElementById('search-input');
        const categorySelect = document.getElementById('category-select');
        const sortSelect = document.getElementById('sort-select');

        if (searchInput) searchInput.value = '';
        if (categorySelect) categorySelect.value = '';
        if (sortSelect) sortSelect.value = 'newest';

        this.updateProductView();
    }

    updateProductView() {
        const productsGrid = document.querySelector('.products-grid[data-catalogue]');
        if (!productsGrid) return;

        const searchTerm = (document.getElementById('search-input')?.value || '').toLowerCase();
        const categorySlug = this.lockedCategory || document.getElementById('category-select')?.value || '';
        const sortBy = document.getElementById('sort-select')?.value || 'newest';

        const clearBtn = document.getElementById('clear-filters-btn');
        if (clearBtn) {
            clearBtn.style.display = (searchTerm || (!this.lockedCategory && categorySlug)) ? 'block' : 'none';
        }

        let filteredProducts = [...this.products];

        if (categorySlug) {
            filteredProducts = filteredProducts.filter(product => product.category_id === categorySlug);
        }

        if (searchTerm) {
            filteredProducts = filteredProducts.filter(product =>
                product.name.toLowerCase().includes(searchTerm) ||
                (product.short_description && product.short_description.toLowerCase().includes(searchTerm))
            );
        }

        switch (sortBy) {
            case 'name_asc':
                filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'name_desc':
                filteredProducts.sort((a, b) => b.name.localeCompare(a.name));
                break;
            default:
                filteredProducts.sort((a, b) => {
                    const aDate = a.$createdAt ? new Date(a.$createdAt).getTime() : 0;
                    const bDate = b.$createdAt ? new Date(b.$createdAt).getTime() : 0;
                    return bDate - aDate;
                });
                break;
        }

        this.renderProductGrid(filteredProducts);
    }

    renderProductGrid(productsToRender) {
        const productsGrid = document.querySelector('.products-grid[data-catalogue]');
        if (!productsGrid) return;

        const productsCountElement = document.getElementById('products-count');
        productsGrid.querySelector('.loading-state')?.remove();

        if (productsCountElement) {
            productsCountElement.textContent = `${productsToRender.length} product${productsToRender.length === 1 ? '' : 's'}`;
        }

        if (productsToRender.length === 0) {
            productsGrid.innerHTML = `
                <div class="no-products">
                    <h3>No Products Found</h3>
                    <p>Your search or filter criteria did not match any products.</p>
                    <button class="no-products-action" onclick="window.productCatalog.clearFilters()">Clear All Filters</button>
                </div>`;
            return;
        }

        productsGrid.innerHTML = productsToRender.map(product => this.createProductCard(product)).join('');
    }

    renderFilters() {
        const selectElement = document.getElementById('category-select');
        if (!selectElement) return;

        const allOption = '<option value="">All Categories</option>';
        const categoryOptions = this.categories
            .map(category => `<option value="${this.escapeHtml(category.slug)}">${this.escapeHtml(category.name)}</option>`)
            .join('');

        selectElement.innerHTML = allOption + categoryOptions;
        selectElement.disabled = false;

        const urlParams = new URLSearchParams(window.location.search);
        const categorySlug = urlParams.get('category');
        if (categorySlug) {
            selectElement.value = categorySlug;
        }
    }

    calculateProductPrice(product) {
        const basePrice = this.parsePrice(product.base_price);
        const selectedAttributes = this.safeParseJSON(product.selected_attributes, []);
        const variantsData = this.safeParseJSON(product.variants_data, []);

        const hasVariants = Array.isArray(selectedAttributes) && selectedAttributes.length > 0 &&
            Array.isArray(variantsData) && variantsData.length > 0;

        if (hasVariants) {
            const variantPrices = variantsData
                .map(variant => this.parsePrice(variant.price))
                .filter(price => price > 0);
            return this.calculateVariantPricing(basePrice, variantPrices);
        }

        return this.calculateSinglePricing(basePrice);
    }

    parsePrice(value) {
        if (value === null || value === undefined || value === '') {
            return 0;
        }

        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : 0;
        }

        const cleaned = `${value}`.replace(/[^\d.]/g, '');
        const parsed = parseFloat(cleaned);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    calculateVariantPricing(basePrice, variantPrices) {
        const allPrices = [...variantPrices];
        if (basePrice > 0) allPrices.push(basePrice);

        if (allPrices.length === 0) {
            return {
                display: 'Request Price',
                hasPrice: false,
                isRange: false,
                cssClass: 'request-price'
            };
        }

        const minPrice = Math.min(...allPrices);
        const maxPrice = Math.max(...allPrices);

        if (minPrice === maxPrice) {
            return {
                display: `MRP: ₹${minPrice.toLocaleString('en-IN')}`,
                hasPrice: true,
                isRange: false,
                cssClass: 'fixed-price'
            };
        }

        return {
            display: `MRP: ₹${minPrice.toLocaleString('en-IN')} - ₹${maxPrice.toLocaleString('en-IN')}`,
            hasPrice: true,
            isRange: true,
            cssClass: 'price-range'
        };
    }

    calculateSinglePricing(basePrice) {
        if (basePrice > 0) {
            return {
                display: `MRP: ₹${basePrice.toLocaleString('en-IN')}`,
                hasPrice: true,
                isRange: false,
                cssClass: 'fixed-price'
            };
        }

        return {
            display: 'Request Price',
            hasPrice: false,
            isRange: false,
            cssClass: 'request-price'
        };
    }

    checkProductHasVariants(product) {
        const selectedAttributes = this.safeParseJSON(product.selected_attributes, []);
        const variantsData = this.safeParseJSON(product.variants_data, []);
        return Array.isArray(selectedAttributes) && selectedAttributes.length > 0 &&
            Array.isArray(variantsData) && variantsData.length > 0;
    }

    generateProductDisplayName(product) {
        if (!this.checkProductHasVariants(product)) {
            return product.name;
        }

        const variantsData = this.safeParseJSON(product.variants_data, []);
        if (variantsData.length === 0) return product.name;

        const firstVariant = variantsData[0];
        if (!firstVariant || !firstVariant.attributes) return product.name;

        const preferredOrder = ['size', 'finish', 'material', 'color'];
        const attributes = Object.values(firstVariant.attributes);

        const orderedValues = [];
        preferredOrder.forEach(preferredId => {
            const match = attributes.find(attr => attr.attributeId === preferredId);
            if (match) {
                orderedValues.push(match.value);
            }
        });

        attributes.forEach(attr => {
            if (!preferredOrder.includes(attr.attributeId)) {
                orderedValues.push(attr.value);
            }
        });

        if (orderedValues.length > 0) {
            return `${product.name} (${orderedValues.join(' - ')})`;
        }

        return product.name;
    }

    createProductCard(product) {
        const category = this.categoriesById.get(product.category_id);
        const categoryName = category ? category.name : (product.category || 'Uncategorized');

        let imageUrl = this.getDefaultProductImage();
        const images = this.safeParseJSON(product.images, []);
        if (images.length > 0) {
            imageUrl = images[0];
        }

        const priceInfo = this.calculateProductPrice(product);
        const hasVariants = this.checkProductHasVariants(product);
        // Cards show the base name; the modal appends the chosen variant.
        const displayName = product.name;

        return `
            <div class="product-card" data-product-id="${this.escapeHtml(product.$id)}" onclick="window.productModal.open(this.dataset.productId)">
                <div class="product-image-container">
                    <img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(displayName)}" class="product-image" loading="lazy" onerror="this.src='${this.getDefaultProductImage()}'; this.onerror=null;">
                </div>
                <div class="product-info">
                    <span class="product-category">${this.escapeHtml(categoryName)}</span>
                    <div class="badges-container">
                        ${hasVariants ? '<span class="variant-badge">Multiple Options</span>' : ''}
                        ${!priceInfo.hasPrice ? '<span class="request-price-badge">Request Price</span>' : ''}
                    </div>
                    <h3 class="product-name">${this.escapeHtml(displayName)}</h3>
                    <p class="product-description">${this.escapeHtml(product.short_description || '')}</p>
                    <div class="product-price ${priceInfo.cssClass}">${priceInfo.display}</div>
                    <button class="get-quote-btn" type="button">View details</button>
                </div>
            </div>`;
    }

    renderCategories() {
        const container = document.getElementById('product-categories-container');
        if (!container) return;

        container.querySelector('.categories-loading')?.remove();

        if (this.categories.length === 0) {
            container.innerHTML = '<p>No product categories are available at the moment.</p>';
            return;
        }

        container.innerHTML = this.categories.map(category => this.createCategoryCard(category)).join('');
    }

    // Static landing pages exist for the main categories; anything else falls back to the filtered catalogue.
    categoryPageUrl(slug) {
        const pages = ['stainless-steel-sofa-legs', 'cabinet-handles', 'steel-knobs'];
        return pages.includes(slug) ? `/${slug}.html` : `/products.html?category=${encodeURIComponent(slug)}`;
    }

    createCategoryCard(category) {
        const imageUrl = category.image_url || this.getDefaultCategoryImage();
        const productCount = this.products.filter(p => p.category_id === category.$id).length;

        return `
            <a class="category-card" href="${this.escapeHtml(this.categoryPageUrl(category.slug))}">
                <div class="category-card__media">
                    <img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(category.name)}" loading="lazy" onerror="this.src='${this.getDefaultCategoryImage()}'; this.onerror=null;">
                </div>
                <div class="category-card__body">
                    <h3>${this.escapeHtml(category.name)}</h3>
                    <span class="category-card__meta">${productCount} product${productCount === 1 ? '' : 's'}</span>
                    <span class="category-card__link">View range <i class="ph ph-arrow-right"></i></span>
                </div>
            </a>`;
    }

    renderTrendingProducts() {
        const container = document.getElementById('trending-products-container');
        if (!container) return;

        container.querySelector('.categories-loading')?.remove();

        const sorted = [...this.products].sort((a, b) => {
            const aDate = a.$createdAt ? new Date(a.$createdAt).getTime() : 0;
            const bDate = b.$createdAt ? new Date(b.$createdAt).getTime() : 0;
            return bDate - aDate;
        });

        const trending = sorted.slice(0, 6);

        if (trending.length === 0) {
            container.innerHTML = '<p class="no-products">No products available yet. Check back soon.</p>';
            return;
        }

        container.innerHTML = trending.map(product => this.createProductCard(product)).join('');
    }

    injectProductStructuredData() {
        if (this.products.length === 0) return;

        const origin = window.location.origin;
        const listed = this.lockedCategory
            ? this.products.filter(product => product.category_id === this.lockedCategory)
            : this.products;
        const items = listed.slice(0, 100).map((product, index) => {
            const images = this.safeParseJSON(product.images, []).slice(0, 3);
            const variantPrices = this.safeParseJSON(product.variants_data, [])
                .map(variant => this.parsePrice(variant.price));
            const prices = [...variantPrices, this.parsePrice(product.base_price)].filter(price => price > 0);

            const item = {
                '@type': 'Product',
                name: product.name,
                brand: { '@type': 'Brand', name: 'Sevad Furniture Fittings' },
                category: product.category
            };
            if (product.short_description) item.description = product.short_description;
            if (images.length > 0) item.image = images;

            if (prices.length > 0) {
                const low = Math.min(...prices);
                const high = Math.max(...prices);
                item.offers = low === high
                    ? { '@type': 'Offer', price: low, priceCurrency: 'INR', url: `${origin}/products.html` }
                    : { '@type': 'AggregateOffer', lowPrice: low, highPrice: high, priceCurrency: 'INR', offerCount: prices.length };
            }

            return { '@type': 'ListItem', position: index + 1, item };
        });

        document.getElementById('product-structured-data')?.remove();
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'product-structured-data';
        script.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Sevad Furniture Fittings Products',
            itemListElement: items
        });
        document.head.appendChild(script);
    }

    showError(error) {
        const errorHTML = `
            <div style="text-align: center; padding: 40px 20px; color: #7f8c8d;">
                <h3>Oops! Something went wrong.</h3>
                <p>${this.escapeHtml(error?.message || 'We could not load the catalog. Please try again later.')}</p>
            </div>`;

        document.querySelectorAll('.categories-loading, .loading-state').forEach(el => {
            el.parentElement.innerHTML = errorHTML;
        });
    }

    generateShortDescription(description) {
        if (!description) return '';
        const trimmed = description.trim();
        return trimmed.length > 140 ? `${trimmed.slice(0, 137)}...` : trimmed;
    }

    escapeHtml(text) {
        return String(text ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    getDefaultCategoryImage() {
        return 'https://placehold.co/600x600/f0f0f0/ccc?text=Category';
    }

    getDefaultProductImage() {
        return 'https://placehold.co/600x600/f0f0f0/ccc?text=Product';
    }

    safeParseJSON(value, fallback) {
        if (value === null || value === undefined) {
            return fallback;
        }

        if (typeof value === 'object') {
            return value;
        }

        if (typeof value === 'string' && value.trim() !== '') {
            try {
                return JSON.parse(value);
            } catch (error) {
                console.warn('JSON parse failed, falling back.', error);
                return fallback;
            }
        }

        return fallback;
    }

    slugify(value) {
        if (!value) return '';
        return value
            .toString()
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }
}

document.addEventListener('DOMContentLoaded', () => {
    if (!window.productCatalog) {
        window.productCatalog = new ProductCatalog();
        window.demo = window.productCatalog;
    }
});
